import WarningMessage from '@Selectors/builder/myArmyList/make/unit/edit/warning/WarningMessage';
import { groupedSelectedElementByScope } from '@Selectors/builder/myArmyList/common/constraints/elements/selectable/selected-element-by-scope';
import {
  ConstrainedObject,
  Constraint,
  ConstraintField,
  ConstraintModifier,
  ConstraintModifierAction,
  ConstraintScope,
  ConstraintType,
  GroupedSelectedElement,
  SelectedElement,
} from '@Models';
import { allConstraintModifierByConstraint } from '@Selectors/builder/myArmyList/common/constraints/elements/constrainable/all-constraint-modifier-by-constraint';
import { modifierIsActive } from '@Selectors/builder/myArmyList/common/constraints/conditions/modifier-is-active';
import { howManyRepeat } from '@Selectors/builder/myArmyList/common/constraints/repeater/how-many-repeat';

export const createMessageIfConstraintFail = (
  selectedElement: GroupedSelectedElement,
  constraint: Constraint,
  element: ConstrainedObject,
  armyListUnitIds: string[],
  activeModifiers: ConstraintModifier[],
): WarningMessage | null => {
  let value = constraint.value;

  activeModifiers.forEach((modifier) => {
    switch (modifier.action) {
      case ConstraintModifierAction.Decrement:
        value -= parseInt(modifier.value);
        break;
      case ConstraintModifierAction.Increment:
        value += parseInt(modifier.value);
        break;
      case ConstraintModifierAction.Set:
        value = parseInt(modifier.value);
        break;
    }
  });

  let count = 0;
  switch (constraint.field) {
    case ConstraintField.Selections:
      count = selectedElement.count;
      break;
    case ConstraintField.Models:
      count = selectedElement.quantity;
      break;
    case ConstraintField.Points:
      count = selectedElement.points;
      break;
  }

  const message = `${value}${constraint.field === ConstraintField.Points ? ' Points in' : ''} ${element.name}${constraint.scope == ConstraintScope.Roster ? '' : ` in your ${element.parent.name}`}, you have ${count}${constraint.field === ConstraintField.Points ? ' Points' : ''}.`;

  if (constraint.type === ConstraintType.Max && count > value) {
    return {
      type: `${constraint.type}-constraint-${constraint.scope}`,
      message: `You can only have ${message}`,
      armyListUnitIds: armyListUnitIds,
    } satisfies WarningMessage as WarningMessage;
  } else if (constraint.type === ConstraintType.Min && count < value) {
    return {
      type: `${constraint.type}-constraint-${constraint.scope}`,
      message: `You must have ${message}`,
      armyListUnitIds: armyListUnitIds,
    } satisfies WarningMessage as WarningMessage;
  }

  return null;
};

export const findActiveConstraintModifiers = (
  constraint: Constraint,
  elements: ConstrainedObject[],
  selecteds: SelectedElement[],
  selected: SelectedElement,
): ConstraintModifier[] => {
  return allConstraintModifierByConstraint(elements, constraint.id).flatMap(
    (modifier) => {
      if (modifierIsActive(modifier, elements, selecteds, selected))
        return [modifier];

      const repeatCount = modifier.repeats
        .map((repeater) => {
          return howManyRepeat(repeater, elements, selecteds, selected);
        })
        .reduce((acc, count) => acc + count, 0);

      return [...Array(repeatCount)].map(() => modifier);
    },
  );
};

export const checkContraints = (
  elements: ConstrainedObject[],
  selecteds: SelectedElement[],
): WarningMessage[] => {
  return selecteds
    .flatMap((selected: SelectedElement) => {
      return elements
        .filter(
          (item) =>
            item.type === selected.constrained.type &&
            item.id === selected.constrained.id,
        )
        .flatMap((element) => {
          return element.constraints.flatMap((constraint) => {
            const scopedElements = groupedSelectedElementByScope(
              elements,
              selecteds,
              selected,
              constraint.scope,
            );

            const activeModifiers = findActiveConstraintModifiers(
              constraint,
              elements,
              selecteds,
              selected,
            );

            return scopedElements.flatMap((scopedElement) => {
              return createMessageIfConstraintFail(
                scopedElement,
                constraint,
                element,
                selected.armyListUnitIds,
                activeModifiers,
              );
            });
          });
        });
    })
    .filter((item): item is WarningMessage => !!item);
};
